import { PaginationProps, TableProps } from 'antd';
import { deleteApiWithData, getApi, postApi } from 'apis';
import { SideDrawerWrapper } from 'components/Global';
import TableActionHeader from 'components/Global/TableActionHeader';
import ConfirmDelete from 'components/Global/confirmDeleteModel';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import AddUserBody from './AddUserBody';
import CustomTable from './CustomTable';
import styles from './index.module.scss';
import { useDebounce } from 'utils/hooks';

const UsersTable = () => {

	// State variables
	const [drawerAnimation, setDrawerAnimation] = useState<boolean>(false);
	const [isSideDrawerOpen, setSideDrawerOpen] = useState<boolean>(false);
	const [roleOptions, setRoleOptions] = useState([]);
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(
		Number(localStorage.getItem('userTableSize')) || 10
	);
	const [staffFilter, setStaffFilter] = useState('true');
	const [roleFilter, setRoleFilter] = useState<string | null>(null);
	const [sort, setSort] = useState('');
	const [type, setType] = useState('asc');
	const [drawerInfo, setDrawerInfo] = useState({
		drawerTitle: 'Users',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editSelectedUser, setEditSelectedUser] = useState<any>();
	const [totalUsers, setTotalUsers] = useState(0);
	const [userData, setUserData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
	const [isSearchCancel, setIsSearchCancel] = useState<boolean>(false);

	const debouncedSearch = useDebounce(search, 500);

	const removeDrawerFromDom = () => {
		setSideDrawerOpen(false);
	};

	const openDrawerHandler = () => {
		setDrawerInfo({ drawerTitle: 'Add User' });
		setDrawerAnimation(true);
		setSideDrawerOpen(true);
	};


	const closeDrawerByAnimation = () => {
		setEditSelectedUser(undefined); //!isAddUserLoading &&
		setDrawerAnimation(false); //!isAddUserLoading &&
	};


	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setSearch(value);
		setPage(1);
		if (value.length > 0) {
			setIsSearchCancel(true)
		} else {
			setIsSearchCancel(false)
		}
	};


	const showModal = () => {
		setIsModalOpen(true);
	};

	const setDrawerInfoHandler = (drawerTitle: any) => {
		setDrawerInfo({ drawerTitle });
	};

	const handleOk = () => {
		setIsModalOpen(false);
		setEditSelectedUser(null);
	};

	const handleCancel = () => {
		setIsModalOpen(false); // !isAddUserLoading &&
		setEditSelectedUser(null);
	};

	// Delete user
	const deleteHandler = async () => {
		if (editSelectedUser) {
			try {
				setIsDeleteLoading(true);
				const resp = await deleteApiWithData('/user', { deleteUserId: editSelectedUser.userId });
				toastText(resp?.data?.message ?? 'User deleted successfully', 'success');
			} catch (error: any) {
				toastText(error.response?.data?.message ?? 'Something went wrong in delete user', 'error');
			} finally {
				setIsDeleteLoading(false);
				handleCancel();
				fetchUsers();
			}
		}
	};

	const reinviteUserAction = async (data: any) => {
		setIsLoading(true);
		try {
			await postApi('/user/reinvite-user', {
				reInviteUserId: data.userId,
			});
			toastText('User re-invited successfully', 'success');
		} catch (error) {
			toastText('Something went wrong in re-inviting user', 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const fetchUsers = useCallback(async () => {
		setIsLoading(true);
		const query: Record<string, unknown> = {
			page: page,
			limit: limit,
			search: debouncedSearch,
			sort: sort,
			type: type,
			staffActive: staffFilter,
		};
		if (roleFilter) {
			query.roleId = roleFilter;
		}
		try {
			const res = await getApi('/user', query);
			setUserData(res?.data?.data);
			setTotalUsers(res?.data?.total);
		} catch (error:any) {
			toastText(error?.response?.data?.error?.errorDescription, 'error');
		}finally{
			setIsLoading(false);
		}

	}, [page, limit, debouncedSearch, type, sort, staffFilter, roleFilter]);

	const handleTableChange: TableProps<any>['onChange'] = (
		pagination,
		filters,
		sorter
	) => {
		const sortOrder = Array.isArray(sorter)
			? sorter[0]?.order
			: sorter.order === 'ascend'
				? 'asc'
				: 'desc';
		setType(sortOrder as string);

		const sortField = Array.isArray(sorter)
			? sorter[0]?.field
			: sorter.field === 'name'
				? 'firstName'
				: sorter.field;
		setSort(sortField as string);
	};

	const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
		current,
		pageSize
	) => {
		setPage(current);
		setLimit(pageSize);
	};


	useEffect(() => {
		fetchUsers();
	}, [limit, page, debouncedSearch, sort, type, staffFilter, roleFilter, fetchUsers]);

	const fetchRoles = async () => {
		try {
			const data: any = await getApi('/role');

			if (data.data.data) {
				const roles = data.data.data.map((role: any) => {
					return {
						id: role.id,
						name: role.roleName,
					};
				});
				setRoleOptions(roles);
			}
		} catch (err) {
			// toastText('Something went wrong in fetching roles', 'error');
		}
	};

	useEffect(() => {
		fetchRoles();
	}, []);
	// JSX
	return (
		<>
			<div className={styles['user-table']}>
				<TableActionHeader title={'Users'}>
				</TableActionHeader>

				<CustomTable
					dataSource={userData}
					handleTableChange={handleTableChange}
					onShowSizeChange={onShowSizeChange}
					isLoading={isLoading}
					search={search}
					totalRecords={totalUsers}
					performSearchHandler={performSearchHandler}
					performStaffFilterHandler={(value: string) => {
						setStaffFilter(value);
						setPage(1);
					}}
					staffFilterValue={staffFilter}
					roleOptions={roleOptions}
					roleFilterValue={roleFilter}
					performRoleFilterHandler={(value: string | null) => {
						setRoleFilter(value);
						setPage(1);
					}}
					reinviteUserAction={reinviteUserAction}
					openDrawerHandler={openDrawerHandler}
					setDrawerInfoHandler={setDrawerInfoHandler}
					setEditSelectedUser={setEditSelectedUser}
					showModal={showModal}
					currentPage={page}
					pageSize={limit}
					isSearchCancel={isSearchCancel}
					setIsSearchCancel={setIsSearchCancel}
					setSearch={setSearch}
				/>
			</div>
			<ConfirmDelete
				handleCancel={handleCancel}
				handleOk={handleOk}
				isModalOpen={isModalOpen}
				deleteHandler={deleteHandler}
				isLoading={isDeleteLoading} 
				selectedDeleteData={editSelectedUser?.name}
				deleteHeading='User'
			/>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerInfo.drawerTitle}
					position="right"
					width="half"
				>
					<AddUserBody
						closeDrawerByAnimation={closeDrawerByAnimation}
						editSelectedUser={editSelectedUser}
						setEditSelectedUser={setEditSelectedUser}
						fetchUsers={fetchUsers}
						roleOptions={roleOptions}
					/>
				</SideDrawerWrapper>
			)}
		</>
	);
};

export default UsersTable;
